import dcmjs from 'dcmjs';
import { toJpeg } from 'html-to-image';
import { useCallback, useState } from 'react';

import { CommandsManager, DicomMetadataStore, ExtensionManager, ServicesManager, useUserAuthentication } from '@core';
import { base64Coded, base64ToBlob, detectMimeTypeBase64 } from '@utils';

const { denaturalizeDataset, naturalizeDataset } = dcmjs.data.DicomMetaDictionary;

const naturalizeTag = [
	'PatientID',
	'PatientName',
	'PatientBirthDate',
	'PatientSex',
	'StudyDate',
	'StudyTime',
	'ReferringPhysicianName',
	'StudyID',
	'AccessionNumber',
	'SeriesNumber',
	'ConversionType',
	'InstanceNumber',
	'PatientOrientation',
	'StudyInstanceUID',
	'SeriesInstanceUID',
	'SamplesPerPixel',
];

export const useSaveKeyImage = (
	ref,
	servicesManager: ServicesManager,
	_commandsManager: CommandsManager,
	extensionManager: ExtensionManager,
	hide: () => void,
) => {
	const { displaySetService, uiNotificationService } = servicesManager.services;
	const { session } = useUserAuthentication();
	const [isFetching, setIsFetching] = useState(false);

	const getImageURL = useCallback(async (ref) => {
		let dataUrl;
		// IMAGE DATA
		if (ref.current?.currentSrc) {
			dataUrl = ref?.current?.currentSrc;
		} else if (ref.current) {
			dataUrl = await toJpeg(ref.current);
		}
		return dataUrl;
	}, []);

	const getImageBlob = useCallback(async () => {
		const dataUrl = await getImageURL(ref);
		const base64 = base64Coded(dataUrl);
		const mimeType = detectMimeTypeBase64(base64);
		const blob = base64ToBlob(base64, mimeType);

		return blob;
	}, [ref, getImageURL]);

	// Hàm tạo payload `multipart/related`
	const createMultipartPayload = useCallback(
		async (boundary: string) => {
			const keyImageDisplaySet = displaySetService.getActiveDisplaySets().find((ds) => ds.SeriesDescription === 'KEY IMAGE');
			const { StudyInstanceUID, SeriesInstanceUID } = keyImageDisplaySet ?? displaySetService.getActiveDisplaySets()[0];
			const blob = await getImageBlob();
			// METADATA
			const seriesData = DicomMetadataStore.getSeries(StudyInstanceUID, SeriesInstanceUID);
			console.log('createMultipartPayload', seriesData);
			const metadata = {};

			naturalizeTag.forEach((tagName) => {
				if (seriesData[tagName]) {
					metadata[tagName] = seriesData[tagName];
				}
			});
			if (!keyImageDisplaySet) {
				metadata['SeriesInstanceUID'] = undefined;
			}
			metadata['ImageType'] = 'SECONDARY';
			// metadata['SamplesPerPixel'] = '3';
			// metadata['SeriesDescription'] = 'KEY_IMAGE';
			// metadata['SOPInstanceUID'] = '1.3.6.1.4.1.9328.50.51.267485233220005486555';
			// metadata['SOPClassUID'] = '1.2.840.10008.5.1.4.1.1.555';
			// metadata['PixelData'] = 'uri0';

			const denaturalizeMetadata = [metadata].map((item) => denaturalizeDataset(item));
			denaturalizeMetadata[0]['7FE00010'] = { vr: 'OB', BulkDataURI: 'uri0' };

			const fileArrayBuffer = await blob.arrayBuffer();
			const fileBinary = new Uint8Array(fileArrayBuffer);
			const metadataPart = `--${boundary}\r\nContent-Type: application/dicom+json\r\n\r\n${JSON.stringify(denaturalizeMetadata)}\r\n`;
			const filePart = `--${boundary}\r\nContent-Type: ${blob.type}\r\nContent-Location: uri0\r\n\r\n`;
			const closingBoundary = `--${boundary}--\r\n`;
			const payload = new Blob([metadataPart, filePart, fileBinary, `\r\n${closingBoundary}`], {
				type: `multipart/related;type=application/dicom boundary=${boundary}`,
			});

			return payload;
		},
		[displaySetService, getImageBlob],
	);

	const sendToStore = useCallback(
		async (payload, boundary) => {
			console.log('Send key image to store', payload);
			const sessionId = session.id;
			const dataSource = extensionManager.getActiveDataSource()[0];
			const url = `${dataSource.getConfig().apiRoot}/session/${sessionId}/studies`;
			const res = await fetch(url, {
				method: 'POST',
				headers: {
					'Content-Type': `multipart/related; type="application/dicom+json"; boundary=${boundary}`,
				},
				body: payload,
			});
			if (res.ok) {
				try {
					const naturalizedKeyimageMetadata = (await res.json()).map(naturalizeDataset);
					naturalizedKeyimageMetadata.forEach((instance) => {
						instance.wadoRoot = dataSource.getConfig().wadoRoot;
						instance.wadoUri = dataSource.getConfig().wadoUri;
						const imageId = dataSource.getImageIdsForInstance({
							instance,
						});
						instance.imageId = imageId;
					});

					const { StudyInstanceUID, SeriesInstanceUID } = naturalizedKeyimageMetadata[0];

					DicomMetadataStore.addInstances(naturalizedKeyimageMetadata);

					const seriesData = DicomMetadataStore.getSeries(StudyInstanceUID, SeriesInstanceUID);
					const dataUrl = await getImageURL(ref);

					if (!seriesData) {
						DicomMetadataStore.addSeriesMetadata([
							{
								StudyInstanceUID,
								SeriesInstanceUID,
								thumbnailSrc: dataUrl,
								SeriesDescription: 'KEY IMAGE',
							},
						]);
					}
				} catch (_error) {
					///TODO: handle error
					console.warn('Cannot update key image thumbnail', _error);
				}

				setIsFetching(false);
				hide();
				uiNotificationService.show({
					title: 'Save key image',
					message: 'Save successfully',
					type: 'info',
					position: 'topRight',
					duration: 3000,
				});
			} else {
				const error = (await res.json()).errorMessage;
				uiNotificationService.show({
					title: 'Save key image',
					message: error ?? 'Error saving key image, please contact administrator.',
					type: 'error',
					position: 'topRight',
					duration: 3000,
				});
			}
		},
		[extensionManager, hide, ref, session.id, uiNotificationService, getImageURL],
	);

	const onDownloadImage = useCallback(async () => {
		const blob = await getImageBlob();
		const blobURL = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = blobURL;
		a.download = 'image.png';
		a.click();
		URL.revokeObjectURL(blobURL);
	}, [getImageBlob]);

	const onSaveKeyImage = useCallback(async () => {
		const boundary = `----WebKitBoundary${Math.random().toString(36).slice(2, 14)}`;
		const payload = await createMultipartPayload(boundary);
		setIsFetching(true);
		sendToStore(payload, boundary);
	}, [createMultipartPayload, sendToStore]);

	return { onSaveKeyImage, onDownloadImage, isFetching };
};
